import React, { useState } from 'react';
import { Box, Button, Flex, Text, IconButton, TextField } from '@radix-ui/themes';
import Icon from '@mdi/react';
import { mdiPlus, mdiCheck, mdiChevronLeft, mdiChevronRight, mdiClose, mdiArrowUp, mdiArrowDown, mdiLightningBolt } from '@mdi/js';
import { v4 as uuidv4 } from 'uuid';
import { useStore } from '../../store/store';
import avatar from '../../assets/avatar.png';

const CreateNonFictionChapters = ({ 
  novelId, 
  topic, 
  style, 
  otherInstructions,
  selectedChapters,
  setSelectedChapters,
  generatedSets,
  currentSetIndex,
  onGenerateChapters,
  onNavigateSet
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [customChapter, setCustomChapter] = useState('');
  const [state, actions] = useStore();

  const handleGenerateChapters = async () => {
    setLoading(true);
    setError(null);
    
    const result = await onGenerateChapters();
    
    if (result.error) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleAddChapter = (chapterTitle) => {
    if (!selectedChapters.some(ch => ch.title === chapterTitle)) {
      setSelectedChapters([...selectedChapters, { 
        id: uuidv4(),
        title: chapterTitle 
      }]);
    }
  };

  const handleRemoveChapter = (index) => {
    const newSelectedChapters = [...selectedChapters];
    newSelectedChapters.splice(index, 1);
    setSelectedChapters(newSelectedChapters);
  };

  const handleMoveUp = (index) => {
    if (index <= 0) return; // Can't move up the first item
    
    const newSelectedChapters = [...selectedChapters];
    [newSelectedChapters[index-1], newSelectedChapters[index]] = 
    [newSelectedChapters[index], newSelectedChapters[index-1]];
    
    setSelectedChapters(newSelectedChapters);
  };
  
  const handleMoveDown = (index) => {
    if (index >= selectedChapters.length - 1) return; // Can't move down the last item
    
    const newSelectedChapters = [...selectedChapters];
    [newSelectedChapters[index], newSelectedChapters[index+1]] = 
    [newSelectedChapters[index+1], newSelectedChapters[index]];
    
    setSelectedChapters(newSelectedChapters);
  };

  const handleAddCustomChapter = () => {
    if (!customChapter.trim()) return;
    
    if (!selectedChapters.some(ch => ch.title === customChapter.trim())) {
      setSelectedChapters([...selectedChapters, {
        id: uuidv4(),
        title: customChapter.trim()
      }]);
      setCustomChapter(''); // Clear the input after adding
    }
  };

  const handleCustomChapterKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddCustomChapter();
    }
  };

  const currentChapters = currentSetIndex >= 0 && currentSetIndex < generatedSets.length
    ? generatedSets[currentSetIndex]
    : [];

  return (
    <>
      {state.novels.length === 0 ?
            <Flex className="tutorial-container" style={{ }}>
                <Flex className={"tutorial-text"}>
                    <img src={avatar} alt="Avatar" className="tutorial-avatar" />
                    <div>Use 'Generate Chapters' to let the AI come up with chapters for the book. You can then add them to the right hand side, where you can also add you own custom chapters and change the order. Feel free to create more than one set on chapters! You can always go back and forth to select the chapters you think are the most fitting!</div>
                </Flex>
            </Flex> : null}

      <Box className="custom-topic-card">
        <Box className="custom-topic-info">
          <Text size="1" className="custom-label">Topic</Text>
          <Text size="3" className="custom-topic-text">{topic || 'Not specified'}</Text>
        </Box>
      </Box>
      <div className="nonfiction-container">
        {/* Left column - Generation and display of chapter sets */}
        <Flex direction="column" className="nonfiction-column custom-column">
          <Box className="nonfiction-content custom-content">
            <Flex justify="between" align="center" className="nonfiction-header custom-header">
              <Text className="nonfiction-title custom-title" as="h3">Generated Chapter Titles</Text>
              <Button 
                className="custom-button"
                onClick={handleGenerateChapters}
                disabled={loading || !topic}
              >
                <Flex align="center" gap="2">
                  {loading ? (
                    <div className="custom-spinner" />
                  ) : (
                    <Icon path={mdiLightningBolt} size={0.8} />
                  )}
                  <span>{generatedSets.length === 0 ? "Generate Chapters" : "Generate New Set"}</span>
                </Flex>
              </Button>
            </Flex>
            

            {error && (
              <Box className="custom-error-message">
                <Icon path={mdiClose} size={0.7} />
                <Text className="custom-error-text">{error}</Text>
              </Box>
            )}

            <Box className="custom-chapters-card">
              {currentChapters.length > 0 && (
                <>
                  <Box className="custom-set-navigation">
                    {generatedSets.length > 1 && (
                      <Flex justify="between" align="center" className="custom-set-controls">
                        <Button 
                          variant="outline" 
                          className="custom-nav-button"
                          disabled={currentSetIndex <= 0}
                          onClick={() => onNavigateSet(-1)}
                        >
                          <Icon path={mdiChevronLeft} size={0.8} />
                          <span>Previous Set</span>
                        </Button>
                        
                        <Text className="custom-set-badge">
                          Set {currentSetIndex + 1} of {generatedSets.length}
                        </Text>
                        
                        <Button 
                          variant="outline"
                          className="custom-nav-button"
                          disabled={currentSetIndex >= generatedSets.length - 1}
                          onClick={() => onNavigateSet(1)}
                        >
                          <span>Next Set</span>
                          <Icon path={mdiChevronRight} size={0.8} />
                        </Button>
                      </Flex>
                    )}
                  </Box>
                  
                  <Box className="custom-chapter-list">
                    {currentChapters.map((chapterTitle, index) => (
                      <div
                        key={`generated-${index}`} 
                        className="custom-chapter-item"
                        onMouseEnter={() => setHoverIndex(`left-${index}`)}
                        onMouseLeave={() => setHoverIndex(null)}
                      >
                        <Flex justify="between" align="center" style={{height: '35px'}}>
                          <Text as={"div"} className="custom-chapter-text">
                            <div className="custom-chapter-number">{index + 1}. {chapterTitle}</div>
                          </Text>
                        </Flex>
                        {(hoverIndex === `left-${index}` || selectedChapters.some(ch => ch.title === chapterTitle)) && (
                            <IconButton 
                              style={{marginTop: '-33px'}}
                              className={`custom-add-button ${selectedChapters.some(ch => ch.title === chapterTitle) ? 'selected' : ''}`}
                              onClick={() => handleAddChapter(chapterTitle)}
                            >
                              <Icon path={selectedChapters.some(ch => ch.title === chapterTitle) ? mdiCheck : mdiPlus} size={0.8} />
                            </IconButton>
                          )}
                      </div>
                    ))}
                  </Box>
                </>
              )}

              {generatedSets.length === 0 && !loading && (
                <Flex direction="column" align="center" justify="center" className="custom-empty-placeholder">
                  <Icon path={mdiLightningBolt} size={1.5} className="custom-empty-icon" />
                  <Text className="custom-empty-text">Click the Generate button to create chapter titles</Text>
                </Flex>
              )}
            </Box>
          </Box>
        </Flex>

        {/* Right column - Selected chapters */}
        <Flex direction="column" className="nonfiction-column custom-column">
          <Box className="nonfiction-content custom-content">
            <div className={"custom-header"}>
            <Text className="nonfiction-title custom-title" as="h3">Selected Chapters</Text>
            </div>

            <Box className="custom-selection-card">
              {selectedChapters.length === 0 ? (
                <>
                <div>
                    <Flex className="custom-add-chapter-form" align="center" gap="2">
                        <div className="custom-add-chapter-input" size="2" style={{ flexGrow: 1 }}>
                            <TextField.Root 
                            placeholder="Enter custom chapter title..." 
                            value={customChapter} 
                            onChange={(e) => setCustomChapter(e.target.value)}
                            onKeyDown={handleCustomChapterKeyDown}
                            />
                        </div>
                        <Button 
                            className="custom-add-chapter-button"
                            disabled={!customChapter.trim()}
                            onClick={handleAddCustomChapter}
                        >
                            Add Custom
                        </Button>
                    </Flex>
                </div>
                <Flex direction="column" align="center" justify="center" className="custom-empty-selection">
                  <Icon path={mdiChevronLeft} size={1.5} className="custom-empty-icon" />
                  <Text className="custom-empty-text">Select chapters from the generated list or add custom chapters</Text>
                </Flex>
                </>
              ) : (
                <>
                  {/* Add custom chapter input field */}
                    <Flex className="custom-add-chapter-form" align="center" gap="2">
                        <div className="custom-add-chapter-input" size="2" style={{ flexGrow: 1 }}>
                            <TextField.Root 
                            placeholder="Enter custom chapter title..." 
                            value={customChapter} 
                            onChange={(e) => setCustomChapter(e.target.value)}
                            onKeyDown={handleCustomChapterKeyDown}
                            />
                        </div>
                        <Button 
                            className="custom-add-chapter-button"
                            disabled={!customChapter.trim()}
                            onClick={handleAddCustomChapter}
                        >
                            Add Custom
                        </Button>
                    </Flex>
                  
                  <Box className="custom-selection-list">
                    {selectedChapters.map((chapter, index) => (
                      <div 
                        key={`selected-${chapter.id}`} 
                        className="custom-selection-item"
                        onMouseEnter={() => setHoverIndex(`right-${index}`)}
                        onMouseLeave={() => setHoverIndex(null)}
                      >
                        <Flex justify="between" align="center">
                          <Text className="custom-selection-text">
                            <span className="custom-selection-number">{index + 1}.</span> 
                            {chapter.title}
                          </Text>
                        </Flex>
                        {hoverIndex === `right-${index}` && (
                            <Flex className="custom-selection-actions">
                              <IconButton 
                                className="custom-action-button"
                                disabled={index === 0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveUp(index);
                                }}
                              >
                                <Icon path={mdiArrowUp} size={0.7} />
                              </IconButton>
                              
                              <IconButton 
                                className="custom-action-button"
                                disabled={index === selectedChapters.length - 1}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveDown(index);
                                }}
                              >
                                <Icon path={mdiArrowDown} size={0.7} />
                              </IconButton>
                              
                              <IconButton 
                                className="custom-remove-button"
                                onClick={() => handleRemoveChapter(index)}
                              >
                                <Icon path={mdiClose} size={0.7} />
                              </IconButton>
                            </Flex>
                          )}
                      </div>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Flex>
      </div>
    </>
  );
};

export default CreateNonFictionChapters;
